import { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { MapPin, RefreshCw, Users, Clock } from 'lucide-react';
import axios from 'axios';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fix for default marker icons in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom marker icons for different roles
const createCustomIcon = (role) => {
  const color = role === 'manager' ? '#3b82f6' : '#10b981'; // Blue for manager, Green for viewer
  return L.divIcon({
    className: 'custom-marker',
    html: `<div style="
      background-color: ${color};
      width: 32px;
      height: 32px;
      border-radius: 50%;
      border: 3px solid white;
      box-shadow: 0 2px 8px rgba(0,0,0,0.3);
      display: flex;
      align-items: center;
      justify-content: center;
    ">
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
        <circle cx="12" cy="7" r="4"></circle>
      </svg>
    </div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32],
  });
};

// Component to fit map bounds to markers
function FitBounds({ locations }) {
  const map = useMap();
  
  useEffect(() => {
    if (locations.length > 0) {
      const bounds = L.latLngBounds(locations.map(loc => [loc.latitude, loc.longitude]));
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [locations, map]);
  
  return null;
}

export default function LiveMapDialog({ open, onClose }) {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const intervalRef = useRef(null);

  const fetchLocations = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/locations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLocations(response.data.locations || []);
      setLastUpdate(new Date());
    } catch (error) {
      console.error('Error fetching locations:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchLocations();
      // Refresh every 10 seconds
      intervalRef.current = setInterval(fetchLocations, 10000);
    }
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [open]);

  const formatTime = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getTimeSince = (timestamp) => {
    if (!timestamp) return '';
    const now = new Date();
    const then = new Date(timestamp);
    const diffMs = now - then;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'Just now';
    if (diffMins === 1) return '1 minute ago';
    return `${diffMins} minutes ago`;
  };

  // Default center (will be overridden by FitBounds if there are locations)
  const defaultCenter = [40.7128, -74.0060]; // New York

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[900px] max-h-[90vh]" data-testid="live-map-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-[#0f172a] flex items-center gap-2">
            <MapPin className="w-6 h-6 text-red-500" />
            Live Team Locations
          </DialogTitle>
        </DialogHeader>

        {/* Stats Bar */}
        <div className="flex items-center justify-between py-2 px-4 bg-slate-50 rounded-lg">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Users className="w-4 h-4" />
              <span><strong>{locations.length}</strong> active users</span>
            </div>
            {lastUpdate && (
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Clock className="w-4 h-4" />
                <span>Updated {formatTime(lastUpdate)}</span>
              </div>
            )}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchLocations}
            disabled={loading}
            data-testid="refresh-map"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Legend */}
        <div className="flex items-center gap-4 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-blue-500 border-2 border-white shadow"></div>
            <span>Manager</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-green-500 border-2 border-white shadow"></div>
            <span>Viewer</span>
          </div>
        </div>

        {/* Map Container */}
        <div className="h-[500px] rounded-lg overflow-hidden border border-slate-200">
          {locations.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center bg-slate-100 text-slate-500">
              <MapPin className="w-12 h-12 mb-4 text-slate-400" />
              <p className="text-lg font-medium">No active users</p>
              <p className="text-sm">Users will appear here when they share their location</p>
            </div>
          ) : (
            <MapContainer
              center={defaultCenter}
              zoom={10}
              style={{ height: '100%', width: '100%' }}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <FitBounds locations={locations} />
              {locations.map((loc) => (
                <Marker
                  key={loc.user_id}
                  position={[loc.latitude, loc.longitude]}
                  icon={createCustomIcon(loc.user_role)}
                >
                  <Popup>
                    <div className="p-2">
                      <p className="font-bold text-slate-800">{loc.user_name}</p>
                      <p className="text-sm text-slate-600 capitalize">{loc.user_role}</p>
                      <p className="text-xs text-slate-500 mt-1">
                        {getTimeSince(loc.timestamp)}
                      </p>
                      <p className="text-xs text-slate-400 mt-1">
                        {loc.latitude.toFixed(6)}, {loc.longitude.toFixed(6)}
                      </p>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          )}
        </div>

        {/* User List */}
        {locations.length > 0 && (
          <div className="max-h-[150px] overflow-y-auto">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {locations.map((loc) => (
                <div
                  key={loc.user_id}
                  className="flex items-center gap-2 p-2 bg-slate-50 rounded-md"
                  data-testid={`user-location-${loc.user_id}`}
                >
                  <div 
                    className={`w-3 h-3 rounded-full ${loc.user_role === 'manager' ? 'bg-blue-500' : 'bg-green-500'}`}
                  ></div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-700 truncate">{loc.user_name}</p>
                    <p className="text-xs text-slate-500">{getTimeSince(loc.timestamp)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex justify-end pt-2">
          <Button onClick={onClose} data-testid="close-live-map">
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
